import React from "react";
import { Check } from "lucide-react";

const PARTNERSHIP_POINTS = [
  "Authentic cultural recipes",
  "High-quality ingredients",
  "Fresh, locally crafted food",
  "A true global experience",
];

const WHY_POINTS = [
  "Authentic flavours from real cultures",
  "Clean, high-quality ingredients",
  "Perfect for everyday and events",
  "Designed to connect people through food",
];

export const Quality = () => {
  return (
    <section
      id="quality"
      data-testid="quality-section"
      className="relative bg-stone-950 py-24 sm:py-32 lg:py-40 border-t border-stone-900"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left column: image */}
        <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
          <div className="relative aspect-[4/5] overflow-hidden bg-stone-900">
            <img
              src="https://images.unsplash.com/photo-1625604086988-6e41981275fa?auto=format&fit=crop&w=1400&q=80"
              alt="Sliced meat on dark wood"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-950/60 via-transparent to-transparent" />
          </div>
          <p className="mt-6 font-serif-display italic text-stone-500 text-sm">
            &ldquo;Every bite is crafted with purpose.&rdquo;
          </p>
        </div>

        {/* Right column: narrative */}
        <div className="lg:col-span-7 space-y-20">
          <div>
            <p className="text-orange-500 font-sans-display text-xs uppercase tracking-[0.32em] mb-6">
              04 — Our Commitment
            </p>
            <h2 className="font-serif-display text-stone-50 text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Quality, <em className="not-italic text-orange-500">not negotiated.</em>
            </h2>
            <p className="mt-8 text-stone-300 font-sans-display text-base sm:text-lg leading-relaxed max-w-2xl">
              Quality is at the heart of everything we do. We source premium
              ingredients and honour traditional cooking methods to create food
              that is both delicious and nutritious. Every bite is crafted with
              purpose — bringing authenticity, culture, and excellence together.
            </p>
          </div>

          <div className="border-t border-stone-800 pt-12">
            <p className="text-orange-500 font-sans-display text-xs uppercase tracking-[0.32em] mb-6">
              05 — Built Through Strong Partnerships
            </p>
            <h3 className="font-serif-display text-stone-50 text-3xl sm:text-4xl leading-[1.05] tracking-tight max-w-2xl">
              Local chefs. Culturally rooted kitchens. Real culinary expertise.
            </h3>
            <p className="mt-6 text-stone-400 font-sans-display text-base leading-relaxed max-w-2xl">
              We work with people who&apos;ve lived these recipes — to deliver
              food that stays true to its origins.
            </p>
            <ul
              className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3"
              data-testid="partnership-list"
            >
              {PARTNERSHIP_POINTS.map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-3 font-sans-display text-stone-200 text-sm"
                >
                  <Check className="h-4 w-4 text-orange-500 mt-1 shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <div className="border-t border-stone-800 pt-12">
            <p className="text-orange-500 font-sans-display text-xs uppercase tracking-[0.32em] mb-6">
              06 — Why Authentic Kitchen
            </p>
            <h3 className="font-serif-display italic text-stone-50 text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight max-w-2xl">
              This isn&apos;t just food. It&apos;s identity.
            </h3>
            <ul
              className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3"
              data-testid="why-list"
            >
              {WHY_POINTS.map((p) => (
                <li
                  key={p}
                  className="flex items-start gap-3 font-sans-display text-stone-200 text-sm"
                >
                  <Check className="h-4 w-4 text-orange-500 mt-1 shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Quality;
